import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ** MUI Components
import IconButton, { type IconButtonPropsSizeOverrides } from '@mui/material/IconButton';
import type { OverridableStringUnion } from '@mui/types';
import Tooltip from '@mui/material/Tooltip';

// ** Hooks
import useClipboard from '../hooks/useClipboard';
import { useSnack } from '../hooks/useSnack';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';

// ** Components
import FaIconUvilo from './FaIconUvilo';

// ** Utils
import clsx from 'clsx';
import mergeSxProps from '../shared/mergeSxProps';

type Props = {
  text: string;
  title?: string;
  dimmed?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
  size?: OverridableStringUnion<'small' | 'medium' | 'large', IconButtonPropsSizeOverrides>;
};

function ClipboardButton({
  text,
  title = 'Copy to clipboard',
  dimmed,
  disabled,
  sx,
  className,
  size = 'small',
}: Props) {
  'use memo';

  const { t } = useTranslation('ui');
  const theme = useTheme();
  const { snack } = useSnack();
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [copied, setCopied] = useState(false);

  const clipboard = useClipboard({
    onSuccess: () => {
      setCopied(true);
      timerRef.current = setTimeout(() => setCopied(false), 3000);
    },
    onError: () => {
      snack.error(t('clipboardCopyFailed'));
    },
  });

  return (
    <Tooltip title={copied ? 'Copied!' : title} className="clipboard-button">
      <IconButton
        className={clsx('clipboard-button', className)}
        size={size}
        disabled={disabled}
        onClick={() => clipboard.copy(text)}
        sx={mergeSxProps(
          {
            opacity: dimmed && !copied ? 0.5 : undefined,
            '&:hover': {
              opacity: 'initial',
            },
            '& svg': {
              transition: [
                theme.transitions.create(['color', 'opacity'], {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.standard,
                }),
              ],
            },
          },
          sx,
        )}
      >
        <FaIconUvilo
          icon={`clipboard${copied ? '-check' : ''}`}
          sx={copied ? { color: String(theme.palette.success.main) } : undefined}
          square
        />
      </IconButton>
    </Tooltip>
  );
}

export default ClipboardButton;
